import _ from 'lodash';
import { defineStore, storeToRefs } from 'pinia';
import { IOutboundMessageData } from '@onereach/ws-client';

import { useSentry } from '@/composables';
import { EventData, EventCallback, EVENT_TYPE, WORKER_LOCKS } from './types';

const AGENT_UI_WORKER_BUNDLE =  (typeof HITL_AGENT_UI_WORKER_NAME !== 'undefined')
  ? HITL_AGENT_UI_WORKER_NAME
  : '';
const AGENT_UI_WORKER_NAME = 'agent-ui-worker';
const HITL_UI_SERVICE = 'hitl';

const useWorkersStore = defineStore('HITL-Workers-Store', {
  state: () => {
    return {
      isDebug: false,
      worker: {} as SharedWorker | Worker,
      tabId: '',
      connectionId: '',
      /** List of "owned" chat ids */
      chatIds: [] as string[],
      /** Sessions claimed or initiated on this tab */
      ownedSessionIds: [] as string[],
      isMainTab: false,
      /** Allows tracking request-responses using await */
      requestsById: new Map<string, { resolve: (e: EventData) => void; }>(),
      callbacks: new Map<string, Array<EventCallback>>(),
    };
  },
});

export const useAgentUIWorker = () => {
  const store = useWorkersStore();
  const { worker, chatIds, ownedSessionIds, isMainTab, connectionId } = storeToRefs(store);
  const { captureMessage } = useSentry();

  /**
   * Worker scripts must be loaded from the same origin as the document. HITL_UI_URL (or discovery)
   * may still point at a canonical hostname while the app is opened via a CNAME/alias, or the host
   * may be missing from HITL_UI_ALIASES — in those cases we keep the configured path but use the
   * page's origin so SharedWorker/Worker construction succeeds.
   */
  const alignWorkerScriptBaseUrl = (configuredBaseUrl: string): string => {
    if (typeof window === 'undefined' || !configuredBaseUrl) {
      return configuredBaseUrl;
    }
    try {
      const base = new URL(configuredBaseUrl);
      const page = new URL(window.location.href);
      if (base.origin === page.origin) {
        return configuredBaseUrl.replace(/\/+$/, '');
      }
      base.protocol = page.protocol;
      base.host = page.host;
      const path = base.pathname === '/' ? '' : base.pathname.replace(/\/$/, '');
      return `${base.origin}${path}`;
    } catch {
      return configuredBaseUrl;
    }
  };

  /**
   * At least one URL should be provided.
   * Make a request to discovery service to get URL prefix.
   * Bundle name looks like ".../core/v0.95.5/agent-ui-worker.min.js"
   */
  const getBundlePath = async (options: {
    humanInTheLoopURL?: string;
    serviceDiscoveryURL?: string;
  }) => {
    const { humanInTheLoopURL = '', serviceDiscoveryURL = '' } = options;
    const getURL = (url: string) => {
      const base = url.replace(/\/+$/, '');
      return `${base}/core/v${HITL_PKG_VERSION}/${AGENT_UI_WORKER_BUNDLE}`;
    };

    /** TODO: Cross-origin workers wont work in Action Desk? */
    if (humanInTheLoopURL) {
      return getURL(alignWorkerScriptBaseUrl(humanInTheLoopURL));
    }

    const params = `serviceName=${HITL_UI_SERVICE}`;
    const discoveryUrl = `${serviceDiscoveryURL}/api/v2?${params}`;
    const response = await fetch(discoveryUrl);
    const discovery = await response.json();
    return getURL(discovery.url);
  };

  /**
   * Local bundles have static name and are served from a static URL like "http://localhost:5501/"
   * Due to security policy we create worker using code which is quite slow but works for development.
   */
  const getLocalWorkerBundle = async () => {
    const response = await fetch (`${HITL_LOCAL_WORKERS_PATH}${AGENT_UI_WORKER_BUNDLE}`);
    const code = await response.text();
    const encode = btoa(unescape(encodeURIComponent(code)));
    return `data:application/javascript;base64,${encode}`;
  };

  /** In case of mobile browsers or Web Views Shared Worker API might not be available */
  const getWorkerConnection = () => {
    return (typeof SharedWorker !== 'undefined') && (store.worker instanceof SharedWorker)
      ? store.worker.port
      : store.worker as Worker;
  }

  const initializeAgentUIWorker = async (args: {
    token: string;
    humanInTheLoopURL: string;
    humanInTheLoopApiURL: string;
    serviceDiscoveryURL: string;
    emBusURL: string;
    isDebug?: boolean;
    isWSDebug?: boolean;
  }) => {
    const {
      token,
      humanInTheLoopURL,
      humanInTheLoopApiURL,
      serviceDiscoveryURL,
      emBusURL,
      isDebug,
      isWSDebug,
    } = args;

    store.isDebug = isDebug || false;
    /* TODO: During development workers are served from a local directory */
    const workerPath = HITL_LOCAL_WORKERS_PATH
      ? await getLocalWorkerBundle()
      : await getBundlePath({ humanInTheLoopURL, serviceDiscoveryURL });

    try {
      /* In some browser Shared Worker API either doesn't exist or not allowed by Security Policy */
      if (typeof SharedWorker !== 'undefined') {
        try {
          store.worker = new SharedWorker(workerPath, { name: AGENT_UI_WORKER_NAME });
          store.worker.port.start();
        } catch (e) {
          console.warn('Shared Worker blocked by Security Policy!', e);
        }
      }

      /* Fallback to default Worker even if we miss on some features */
      const hasSharedWorker = typeof SharedWorker !== 'undefined' && store.worker instanceof SharedWorker;
      if (!hasSharedWorker) {
        store.worker = new Worker(workerPath, { name: AGENT_UI_WORKER_NAME });
      }

      getWorkerConnection().onmessage = onWorkerEvent;
      /* Notify worker about current tab and queue for MAIN role */
      await registerTab();
      claimLeadership();

      getWorkerConnection().postMessage({
        type: EVENT_TYPE.INITIALIZE,
        timestamp: new Date().toUTCString(),
        content: {
          token,
          humanInTheLoopApiURL,
          serviceDiscoveryURL,
          emBusURL,
          isDebug,
          isWSDebug,
        },
      });
    } catch (e) {
      console.error('Error while initializing "Agent UI" worker!', e);
      throw e;
    }
  };

  /**
   * When worker is created we initialize pings by default.
   * After reconnecting we should manually notify worker.
   */
  const initializeStatusPings = () => {
    getWorkerConnection()?.postMessage({
      type: EVENT_TYPE.INITIALIZE_STATUS_PINGS,
      timestamp: new Date().toUTCString(),
      content: {},
    });
  };

  const updateActiveChats = (args: { chatIds: string[]; }) => {
    const { chatIds = [] } = args;
    getWorkerConnection()?.postMessage({
      type: EVENT_TYPE.TAB_SET_ACTIVE_CHATS,
      timestamp: new Date().toUTCString(),
      content: { chatIds }
    });
  };

  const registerOwnedSession = (args: { sessionId: string; }) => {
    getWorkerConnection()?.postMessage({
      type: EVENT_TYPE.TAB_REGISTER_OWNED_SESSION,
      timestamp: new Date().toUTCString(),
      content: args,
    });
  };

  const ownsSession = (sessionId: string) => store.ownedSessionIds.includes(sessionId);

  const expireWSToken = () => {
    const connection = getWorkerConnection();
    if (typeof connection?.postMessage !== 'function') {
      console.warn('[Auth Token] expireWSToken skipped — worker not ready');
      return;
    }
    connection.postMessage({
      type: EVENT_TYPE.TOKEN_EXPIRE,
      timestamp: new Date().toUTCString(),
      content: {},
    });
  };

  const updateWSToken = (token: string) => {
    const connection = getWorkerConnection();
    if (typeof connection?.postMessage !== 'function') {
      console.warn('[Auth Token] updateWSToken skipped — worker not ready');
      return;
    }
    connection.postMessage({
      type: EVENT_TYPE.TOKEN_UPDATE,
      timestamp: new Date().toUTCString(),
      content: { token },
    });
  };

  const reconnectWS = () => {
    const connection = getWorkerConnection();
    if (typeof connection?.postMessage !== 'function') {
      return;
    }
    connection.postMessage({
      type: EVENT_TYPE.WS_RECONNECT,
      timestamp: new Date().toUTCString(),
      content: {},
    });
  };

  /** Entry point for all events incoming from worker */
  const onWorkerEvent = (event: MessageEvent<EventData>) => {
    store.isDebug && console.debug('[Agent UI Worker Composable]', event.data);
    /* A special branch for handling request responses */
    const requestId = event.data.requestId;
    if (requestId && store.requestsById.has(requestId)) {
      const { resolve } = store.requestsById.get(requestId)!;
      resolve(event.data);

      store.requestsById.delete(requestId);
      return;
    }

    /* Main branch with event broadcasting */
    switch (event.data.type) {
      case EVENT_TYPE.WS_CONNECTION_ID:
        store.connectionId = event.data.content.connectionId;
        break;
      /* A rare error related to wake up / sleep cycle when "close" and "connect" events are not sequenced properly */
      case EVENT_TYPE.WS_CONNECTION_ERROR:
        captureMessage('[Agent UI Worker Composable] Error while reading WS connection ID', {
          connectionId: store.connectionId,
        });
        window.location.reload();
        break;
      case EVENT_TYPE.TAB_ASSIGNMENTS_CHANGE:
        const ownChatIds = _.pickBy(event.data.content.chatIds, value => value === store.tabId);
        store.chatIds = Object.keys(ownChatIds);
        break;
      case EVENT_TYPE.TAB_OWNED_SESSIONS_CHANGE:
        const ownSessions = _.pickBy(event.data.content.ownership, value => value === store.tabId);
        store.ownedSessionIds = Object.keys(ownSessions);
        break;
    }

    const callbacks = store.callbacks.get(event.data.type) || [];
    for (const callback of callbacks) {
      callback(event.data);
    }
  };

  /**
   * Allows user to post message and await for a worker response.
   * TODO: We don't handle errors at the moment?
   */
  const postMessageAsRequest = <T>(args: { type: EVENT_TYPE; content?: T; }) => {
    return new Promise((resolve, _reject) => {
      const { type, content = {} } = args;
      const requestId = crypto.randomUUID();
      store.requestsById.set(requestId, { resolve });

      getWorkerConnection().postMessage({
        type,
        content,
        requestId,
        timestamp: new Date().toUTCString(),
      });
    });
  };

  /** Register this tab or iframe in Agent UI Worker */
  const registerTab = () => {
    return new Promise(resolve => {
      const tabId = `${WORKER_LOCKS.TAB_LOCK_PREFIX}${crypto.randomUUID()}`;
      navigator.locks.request(tabId, async () => {
        store.tabId = tabId;
        /* Resolve main function when worker responds */
        await postMessageAsRequest({
          type: EVENT_TYPE.TAB_REGISTER,
          content: { tabId },
        });
        resolve({});
        /* Lock is never released to reduce tab throttling when out of focus (browser-dependent) */
        return new Promise(() => {});
      });
    });
  };

  /**
   * Request Main Lock and hold it indefinitely.
   * Browser will automatically re-assign Main Lock when browser tab is closed.
   */
  const claimLeadership = () => {
    navigator.locks.request(WORKER_LOCKS.MAIN_LOCK, async () => {
      store.isMainTab = true;
      getWorkerConnection().postMessage({ type: EVENT_TYPE.TAB_SET_MAIN });
      return new Promise(() => {});
    });
  };

  const cleanUpWorker = () => {
    store.worker instanceof SharedWorker
      ? store.worker.port.close()
      : (store.worker as Worker).terminate();
    store.worker = {} as SharedWorker;
    store.requestsById.clear();
    store.callbacks.clear();
    store.connectionId = '';
  };

  const subscribe = <T>(event: EVENT_TYPE, callback: EventCallback<T>) => {
    store.callbacks.has(event)
      ? store.callbacks.get(event)?.push(callback)
      : store.callbacks.set(event, [callback]);
  };

  const unsubscribe = <T>(event: EVENT_TYPE, callback: EventCallback<T>) => {
    const callbacks = store.callbacks.get(event);
    if (callbacks && callbacks.length) {
      store.callbacks.set(event, callbacks.filter(i => i !== callback));
    }
  };

  const postWSMessage = (message: IOutboundMessageData) => {
    return getWorkerConnection().postMessage({
      type: EVENT_TYPE.WS_SEND_MESSAGE,
      content: message,
    });
  };

  return {
    agentUIWorker: worker,
    chatIds,
    ownedSessionIds,
    isMainTab,
    connectionId,
    initializeAgentUIWorker,
    initializeStatusPings,
    updateActiveChats,
    registerOwnedSession,
    ownsSession,
    postMessageAsRequest,
    postWSMessage,
    expireWSToken,
    updateWSToken,
    reconnectWS,
    subscribe,
    unsubscribe,
    cleanUpWorker,
  };
};
